import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiSearch,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiUsers,
  FiCheckCircle,
  FiAlertCircle,
  FiInbox,
  FiAlertTriangle,
  FiRefreshCw,
  FiTrendingUp,
  FiLayers,
  FiTag,
  FiUser,
  FiEye,
  FiShield,
  FiPackage,
} from "react-icons/fi";
import AdminHeader from "../../Header/AdminHeader";
import AdminFooter from "../../Footer/AdminFooter";
import AdminLeftMenu from "../../Navbar/AdminLeftMenu";
import styles from "./UserPlan.module.css";
import { userloginDetails } from "../../Utils/AdminAuth";
import { fetchUserAssignedPlans, fetchPlans } from "./UserPlanApi";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = ["Active", "Expired", "Cancelled", "Pending"];

const FILTER_TYPE_ALL = "all";
const FILTER_TYPE_USER = "user_wise";


const deriveStatus = (endDate) => {
  if (!endDate) return "";
  const end = new Date(endDate.replace(" ", "T"));
  if (Number.isNaN(end.getTime())) return "";
  return end.getTime() >= Date.now() ? "active" : "expired";
};

const normalizeAddOns = (addOn) => {
  if (!addOn || typeof addOn !== "object") return [];
  return Object.entries(addOn).map(([key, val]) => ({
    key,
    label: key.replace(/_/g, " "),
    value: val?.value ?? null,
    amount: val?.amount ?? val?.amount_inr ?? null,
    planFor: val?.plan_for ?? null,
    duration: val?.plan_duration ?? null,
    startDate: val?.start_date ?? null,
    endDate: val?.end_date ?? null,
    addOnId: val?.add_on_id ?? null,
  }));
};

const normalizeAssignedPlanRow = (row, index) => {
  const addOns = normalizeAddOns(row.add_on);
  const endDate = row.end_date ?? row.endDate ?? null;
  const planType = row.plan_type ?? row.planType ?? "main_plan";
  const isAddOnType = planType === "add_on";
  const primaryAddOn = addOns[0] || null;

  return {
    id: row._id ?? row.id ?? `${row.user_id ?? "row"}_${row.plan_id ?? index}`,
    userId: row.user_id ?? "",
    userName: row.user_name ?? "",
    planId: row.plan_id ?? row.planId ?? "",
    planTitle: isAddOnType
      ? addOns.map((a) => a.label).join(", ") || row.plan_title || row.planTitle || ""
      : row.plan_title ?? row.planTitle ?? "",
    planFor: isAddOnType
      ? (primaryAddOn?.planFor ?? row.plan_for ?? row.planFor ?? "")
      : row.plan_for ?? row.planFor ?? "",
    planDescription: row.plan_description ?? row.planDescription ?? "",
    planType,
    channel: row.channel ?? "",
    amountPaid: isAddOnType
      ? (primaryAddOn?.amount ?? row.amount_inr ?? row.amountInr ?? row.amount_paid ?? null)
      : row.amount_inr ?? row.amountInr ?? row.amount_paid ?? null,
    duration: isAddOnType
      ? (primaryAddOn?.duration ?? row.plan_duration ?? row.duration ?? "")
      : row.plan_duration ?? row.duration ?? "",
    status: row.status ?? deriveStatus(endDate),
    startDate: isAddOnType
      ? (primaryAddOn?.startDate ?? row.start_date ?? row.startDate ?? null)
      : row.start_date ?? row.startDate ?? null,
    endDate: isAddOnType ? (primaryAddOn?.endDate ?? endDate) : endDate,
    created: row.created ?? row.created_at ?? null,
    updated: row.updated ?? row.updated_at ?? null,
    feeWaiverEnabled: Number(row.platform_fee_waiver_enabled) === 1,
    feeWaiverAmount: row.platform_fee_waiver_amount ?? null,
    addOns,
    isAddOnType,
  };
};

const normalizePlanOption = (p) => ({
  id: p.id ?? p._id ?? p.plan_id,
  title: p.plan_title ?? p.title ?? "",
});

/* ---------------------------------------------------------------------- */
/* Component                                                               */
/* ---------------------------------------------------------------------- */

const UserPlanList = () => {

  const loginData = userloginDetails();
  const userId = loginData?.user?.userId || loginData?.userId;
  const token =
    loginData?.token ||
    loginData?.user?.token ||
    loginData?.user?.authToken ||
    loginData?.user?.accessToken;

  // Data
  const [rows, setRows] = useState([]);

  // Load / error state
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [filterType, setFilterType] = useState(FILTER_TYPE_ALL);
  const [retrUserIdInput, setRetrUserIdInput] = useState("");
  const [retrUserId, setRetrUserId] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [planOptions, setPlanOptions] = useState([]);

  const [page, setPage] = useState(1);

  const [detailsRow, setDetailsRow] = useState(null);

  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});

  const hasActiveFilters =
    search !== "" ||
    typeFilter !== "all" ||
    planFilter !== "all" ||
    statusFilter !== "all" ||
    startDate !== "" ||
    endDate !== "";

  const needsUserId = filterType === FILTER_TYPE_USER;

  /* --------------------------- Toast helpers --------------------------- */

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (toastTimers.current[id]) {
      clearTimeout(toastTimers.current[id]);
      delete toastTimers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (type, message) => {
      const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, type, message }]);
      toastTimers.current[id] = setTimeout(() => removeToast(id), 3800);
    },
    [removeToast]
  );

  useEffect(() => {
    const timers = toastTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  /* ------------------------- Debounce search input ---------------------- */

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  /* --------------------------- Plan filter options ------------------------ */

  useEffect(() => {
    if (!userId || !token) return;
    let cancelled = false;
    fetchPlans({ userId, token })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data ?? res?.plans ?? [];
        setPlanOptions(Array.isArray(list) ? list.map(normalizePlanOption) : []);
      })
      .catch(() => {
        // Non-fatal: the Plan filter dropdown just stays empty.
      });
    return () => {
      cancelled = true;
    };
  }, [userId, token]);

  /* ------------------------------ Data fetch ----------------------------- */

  const loadUserPlans = useCallback(async () => {
    if (!userId || !token) return;

    if (needsUserId && !retrUserId) {
      setRows([]);
      setLoading(false);
      setLoadError(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchUserAssignedPlans({
        userId,
        token,
        filterType,
        ...(needsUserId ? { retrUserId } : {}),
      });

      const list = res?.data ?? res?.userPlans ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeAssignedPlanRow) : []);
    } catch (err) {
      setLoadError(true);
      setRows([]);
      showToast("error", err?.message || "Failed to load user plan list.");
    } finally {
      setLoading(false);
    }
  }, [userId, token, filterType, needsUserId, retrUserId, showToast]);

  useEffect(() => {
    loadUserPlans();
  }, [loadUserPlans]);


  const filteredRows = useMemo(() => {
    let list = [...rows];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          String(r.userId).toLowerCase().includes(q) ||
          (r.userName || "").toLowerCase().includes(q) ||
          (r.planTitle || "").toLowerCase().includes(q)
      );
    }

    if (typeFilter !== "all") list = list.filter((r) => r.planType === typeFilter);
    if (planFilter !== "all") list = list.filter((r) => String(r.planId) === String(planFilter));
    if (statusFilter !== "all") {
      list = list.filter((r) => (r.status || "").toLowerCase() === statusFilter);
    }
    if (startDate) {
      const from = new Date(startDate);
      list = list.filter((r) => r.startDate && new Date(r.startDate.replace(" ", "T")) >= from);
    }
    if (endDate) {
      const to = new Date(endDate);
      to.setHours(23, 59, 59, 999);
      list = list.filter((r) => r.startDate && new Date(r.startDate.replace(" ", "T")) <= to);
    }

    return list;
  }, [rows, search, typeFilter, planFilter, statusFilter, startDate, endDate]);

  const stats = useMemo(() => {
    const totalRevenue = rows.reduce((sum, r) => {
      const amount = Number(r.amountPaid);
      return sum + (Number.isNaN(amount) ? 0 : amount);
    }, 0);

    return {
      totalSubscribers: rows.length,
      mainPlanUsers: rows.filter((r) => r.planType === "main_plan").length,
      addonUsers: rows.filter((r) => r.planType === "add_on").length,
      totalRevenue: rows.length > 0 ? totalRevenue : null,
    };
  }, [rows]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  /* -------------------------------- Filters ------------------------------ */

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setTypeFilter("all");
    setPlanFilter("all");
    setStatusFilter("all");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const handleFilterTypeChange = (nextType) => {
    setFilterType(nextType);
    setPage(1);
    if (nextType === FILTER_TYPE_ALL) {
      setRetrUserId("");
      setRetrUserIdInput("");
    }
  };

  const handleApplyUserId = () => {
    const trimmed = retrUserIdInput.trim();
    if (!trimmed) {
      showToast("error", "Enter a User ID to look up their assigned plans.");
      return;
    }
    setRetrUserId(trimmed);
    setPage(1);
  };

  /* ------------------------------ Pagination ------------------------------ */

  const getPageNumbers = () => {
    const pages = [];
    const windowSize = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - windowSize && i <= page + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "...") {
        pages.push("...");
      }
    }
    return pages;
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  /* --------------------------------------------------------------------- */
  /* Render helpers                                                        */
  /* --------------------------------------------------------------------- */

  const renderTypeBadge = (type) =>
    type === "add_on" ? (
      <span className={`${styles.badge} ${styles.badgeAddon}`}>Add-on</span>
    ) : (
      <span className={`${styles.badge} ${styles.badgeMain}`}>Main</span>
    );

  // const renderStatusBadge = (status) => {
  //   const normalized = (status || "").toLowerCase();
  //   const map = {
  //     active: styles.badgeActive,
  //     expired: styles.badgeExpired,
  //     cancelled: styles.badgeCancelled,
  //     pending: styles.badgePending,
  //   };
  //   const cls = map[normalized] || styles.badgeInactive;
  //   const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "\u2014";
  //   return <span className={`${styles.badge} ${cls}`}>{label}</span>;
  // };

  const renderStatusBadge = (status) => {
    const normalized = String(status ?? "").toLowerCase();
    const map = {
      active: styles.badgeActive,
      expired: styles.badgeExpired,
      cancelled: styles.badgeCancelled,
      pending: styles.badgePending,
    };
    const cls = map[normalized] || styles.badgeInactive;
    const label = status ? String(status).charAt(0).toUpperCase() + String(status).slice(1) : "—";
    return <span className={`${styles.badge} ${cls}`}>{label}</span>;
  };

  const formatCurrency = (value) => {
    const num = Number(value);
    if (value === null || value === undefined || Number.isNaN(num)) return "\u2014";
    return `\u20B9${num.toLocaleString("en-IN")}`;
  };

  const formatDate = (value) => {
    if (!value) return "\u2014";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatDateTime = (value) => {
    if (!value) return "\u2014";
    const d = new Date(value.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPlanFor = (value) =>
    value ? value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "\u2014";

  /* --------------------------------------------------------------------- */
  /* Render                                                                 */
  /* --------------------------------------------------------------------- */

  return (
    <div className="wrapper">
      <AdminLeftMenu />

      <div className="main">
        <AdminHeader />

        <main className="content">
          <div className="container-fluid p-0">
            <div className={styles.wrapper}>
              {/* Toasts */}
              <div className={styles.toastContainer} aria-live="polite">
                {toasts.map((t) => (
                  <div
                    key={t.id}
                    className={`${styles.toast} ${t.type === "success" ? styles.toastSuccess : styles.toastError
                      }`}
                    role="status"
                  >
                    <span className={styles.toastIcon}>
                      {t.type === "success" ? <FiCheckCircle /> : <FiAlertCircle />}
                    </span>
                    <span>{t.message}</span>
                    <button
                      type="button"
                      className={styles.toastClose}
                      onClick={() => removeToast(t.id)}
                      aria-label="Dismiss notification"
                    >
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>

              {/* Page header */}
              <div className={styles.pageHeader}>
                <div>
                  <h1 className={styles.pageTitle}>User Plan List</h1>
                  <p className={styles.pageDescription}>
                    View the plans currently assigned to users and monitor subscription details.
                  </p>
                </div>
              </div>

              {/* Who to fetch: all users vs one specific user */}
              {/*
              <div className={styles.toolbar}>
                <div className={styles.radioCardGroup} role="radiogroup" aria-label="Scope">
                  <label
                    className={`${styles.radioCard} ${
                      filterType === FILTER_TYPE_ALL ? styles.radioCardSelected : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="filterType"
                      value={FILTER_TYPE_ALL}
                      checked={filterType === FILTER_TYPE_ALL}
                      onChange={() => handleFilterTypeChange(FILTER_TYPE_ALL)}
                    />
                    All Users
                  </label>
                  <label
                    className={`${styles.radioCard} ${
                      filterType === FILTER_TYPE_USER ? styles.radioCardSelected : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="filterType"
                      value={FILTER_TYPE_USER}
                      checked={filterType === FILTER_TYPE_USER}
                      onChange={() => handleFilterTypeChange(FILTER_TYPE_USER)}
                    />
                    Specific User
                  </label>
                </div>

                {needsUserId && (
                  <div className={styles.searchBox}>
                    <FiUser />
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Enter User ID e.g. 6737"
                      value={retrUserIdInput}
                      onChange={(e) => setRetrUserIdInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleApplyUserId()}
                      aria-label="User ID"
                    />
                  </div>
                )}

                {needsUserId && (
                  <button type="button" className={styles.primaryButton} onClick={handleApplyUserId}>
                    Look Up Plans
                  </button>
                )}
              </div>
              */}

              {needsUserId && !retrUserId && !loading && (
                <div className={styles.stateBox}>
                  <span className={styles.stateIcon}>
                    <FiUser />
                  </span>
                  <p className={styles.stateTitle}>Enter a User ID to view their assigned plans.</p>
                </div>
              )}

              {(!needsUserId || retrUserId) && (
                <>
                  {/* Stats */}
                  <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                      <span className={styles.statIcon}>
                        <FiUsers />
                      </span>
                      <div>
                        <p className={styles.statLabel}>Total Subscribers</p>
                        <p className={styles.statValue}>
                          {stats.totalSubscribers.toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statIcon}>
                        <FiLayers />
                      </span>
                      <div>
                        <p className={styles.statLabel}>Main Plan Users</p>
                        <p className={styles.statValue}>
                          {stats.mainPlanUsers.toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                    <div className={styles.statCard}>
                      <span className={styles.statIcon}>
                        <FiTag />
                      </span>
                      <div>
                        <p className={styles.statLabel}>Add-on Users</p>
                        <p className={styles.statValue}>
                          {stats.addonUsers.toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                    {stats.totalRevenue !== null && (
                      <div className={styles.statCard}>
                        <span className={styles.statIcon}>
                          <FiTrendingUp />
                        </span>
                        <div>
                          <p className={styles.statLabel}>Total Revenue</p>
                          <p className={styles.statValue}>{formatCurrency(stats.totalRevenue)}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Toolbar */}
                  <div className={styles.toolbar}>
                    <div className={styles.searchBox}>
                      <FiSearch />
                      <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search by user name or plan..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        aria-label="Search user plans"
                      />
                    </div>

                    <select
                      className={styles.selectInput}
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(e.target.value);
                        setPage(1);
                      }}
                      aria-label="Filter by plan type"
                    >
                      <option value="all">All Types</option>
                      <option value="main_plan">Main Plan</option>
                      <option value="add_on">Add-on Plan</option>
                    </select>

                    <select
                      className={styles.selectInput}
                      value={planFilter}
                      onChange={(e) => {
                        setPlanFilter(e.target.value);
                        setPage(1);
                      }}
                      aria-label="Filter by plan"
                    >
                      <option value="all">All Plans</option>
                      {planOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.title}
                        </option>
                      ))}
                    </select>

                    <select
                      className={styles.selectInput}
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      aria-label="Filter by status"
                    >
                      <option value="all">All Status</option>
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s.toLowerCase()}>
                          {s}
                        </option>
                      ))}
                    </select>

                    <input
                      type="date"
                      className={styles.dateInput}
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e.target.value);
                        setPage(1);
                      }}
                      aria-label="From date"
                    />
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e.target.value);
                        setPage(1);
                      }}
                      aria-label="To date"
                    />

                    {hasActiveFilters && (
                      <button type="button" className={styles.clearFiltersButton} onClick={clearFilters}>
                        <FiX /> Clear Filters
                      </button>
                    )}
                  </div>

                  {/* Table */}
                  <div className={styles.tableCard}>
                    <div className={styles.tableScroll}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            {/* <th>User ID</th> */}
                            <th>User Name</th>
                            <th>Plan</th>
                            <th>Amount Paid</th>
                            <th>Plan Duration</th>
                            {/* <th>Status</th> */}
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading &&
                            Array.from({ length: 8 }).map((_, i) => (
                              <tr key={`sk_${i}`}>
                                {Array.from({ length: 7 }).map((__, j) => (
                                  <td key={j}>
                                    <div
                                      className={styles.skeletonCell}
                                      style={{ width: j === 1 ? "80%" : "55%" }}
                                    />
                                  </td>
                                ))}
                              </tr>
                            ))}

                          {!loading && loadError && (
                            <tr>
                              <td colSpan={7}>
                                <div className={styles.stateBox}>
                                  <span className={styles.stateIcon}>
                                    <FiAlertTriangle />
                                  </span>
                                  <p className={styles.stateTitle}>Unable to load user plan data.</p>
                                  <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={loadUserPlans}
                                  >
                                    <FiRefreshCw /> Retry
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}

                          {!loading && !loadError && pagedRows.length === 0 && !hasActiveFilters && (
                            <tr>
                              <td colSpan={9}>
                                <div className={styles.stateBox}>
                                  <span className={styles.stateIcon}>
                                    <FiInbox />
                                  </span>
                                  <p className={styles.stateTitle}>No user plans found.</p>
                                  <p className={styles.stateSubtitle}>
                                    Users with purchased or assigned plans will appear here.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}

                          {!loading && !loadError && pagedRows.length === 0 && hasActiveFilters && (
                            <tr>
                              <td colSpan={7}>
                                <div className={styles.stateBox}>
                                  <span className={styles.stateIcon}>
                                    <FiSearch />
                                  </span>
                                  <p className={styles.stateTitle}>No users match your current filters.</p>
                                  <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}

                          {!loading &&
                            !loadError &&
                            pagedRows.map((row) => (
                              <tr key={row.id}>
                                {/* <td>{row.userId}</td> */}
                                <td>{row.userName || "\u2014"}</td>
                                <td>
                                  <p className={styles.cellPrimary} style={{ marginBottom: 4 }}>
                                    {row.planTitle}
                                  </p>
                                  {renderTypeBadge(row.planType)}
                                  {!row.isAddOnType && row.addOns.length > 0 && (
                                    <span className={styles.cellSecondary} style={{ marginLeft: 6 }}>
                                      +{row.addOns.length} add-on{row.addOns.length > 1 ? "s" : ""}
                                    </span>
                                  )}
                                </td>
                                <td>{formatCurrency(row.amountPaid)}</td>
                                <td>{row.duration || "—"}</td>
                                <td>{formatDate(row.startDate)}</td>
                                <td>{formatDate(row.endDate)}</td>
                                <td>
                                  <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    onClick={() => setDetailsRow(row)}
                                  >
                                    <FiEye /> View More
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {!loading && !loadError && total > 0 && (
                      <div className={styles.paginationBar}>
                        <span className={styles.paginationInfo}>
                          Showing {rangeStart} to {rangeEnd} of {total} subscriptions
                        </span>
                        <div className={styles.paginationControls}>
                          <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            aria-label="Previous page"
                          >
                            <FiChevronLeft />
                          </button>
                          {getPageNumbers().map((p, idx) =>
                            p === "..." ? (
                              <span key={`e_${idx}`} className={styles.pageEllipsis}>
                                ...
                              </span>
                            ) : (
                              <button
                                key={p}
                                type="button"
                                className={`${styles.pageButton} ${p === page ? styles.pageButtonActive : ""
                                  }`}
                                onClick={() => setPage(p)}
                                aria-current={p === page ? "page" : undefined}
                              >
                                {p}
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            className={styles.pageButton}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            aria-label="Next page"
                          >
                            <FiChevronRight />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* View More details modal */}
              {detailsRow && (
                <div
                  className={styles.modalOverlay}
                  onMouseDown={(e) => e.target === e.currentTarget && setDetailsRow(null)}
                >
                  <div
                    className={styles.modalCard}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="planDetailsTitle"
                  >
                    <div className={styles.modalHeader}>
                      <h2 className={styles.modalTitle} id="planDetailsTitle">
                        {detailsRow.planTitle} — Plan Details
                      </h2>
                      <button
                        type="button"
                        className={styles.modalCloseButton}
                        onClick={() => setDetailsRow(null)}
                        aria-label="Close"
                      >
                        <FiX />
                      </button>
                    </div>

                    <div className={styles.modalBody}>
                      <div className={styles.formSection}>
                        <p className={styles.formSectionTitle}>Overview</p>
                        <div className={styles.detailsGrid}>
                          <div>
                            <p className={styles.statLabel}>User ID</p>
                            <p className={styles.cellPrimary}>{detailsRow.userId}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>Plan Type</p>
                            <p>{renderTypeBadge(detailsRow.planType)}</p>
                          </div>
                          {/* <div>
                            <p className={styles.statLabel}>Status</p>
                            <p>{renderStatusBadge(detailsRow.status)}</p>
                          </div> */}

                          {/* for addon plan type dont show channel field in form */}
                          {detailsRow.planType !== "add_on" && (
                            <div>
                              <p className={styles.statLabel}>Channel</p>
                              <p className={styles.cellPrimary}>{detailsRow.channel || "\u2014"}</p>
                            </div>
                          )}
                          <div>
                            <p className={styles.statLabel}>Plan For</p>
                            <p className={styles.cellPrimary}>{formatPlanFor(detailsRow.planFor)}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>Duration</p>
                            <p className={styles.cellPrimary}>{detailsRow.duration || "\u2014"}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>Amount Paid</p>
                            <p className={styles.cellPrimary}>{formatCurrency(detailsRow.amountPaid)}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>Start Date</p>
                            <p className={styles.cellPrimary}>{formatDate(detailsRow.startDate)}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>End Date</p>
                            <p className={styles.cellPrimary}>{formatDate(detailsRow.endDate)}</p>
                          </div>
                        </div>

                        {detailsRow.planDescription && (
                          <>
                            <p className={styles.statLabel} style={{ marginTop: 14 }}>
                              Description
                            </p>
                            <p className={styles.cellSecondary}>{detailsRow.planDescription}</p>
                          </>
                        )}
                      </div>

                      <div className={styles.formSection}>
                        <p className={styles.formSectionTitle}>
                          <FiShield style={{ marginRight: 6, verticalAlign: "-2px" }} />
                          Platform Fee Waiver
                        </p>
                        <div className={styles.detailsGrid}>
                          <div>
                            <p className={styles.statLabel}>Enabled</p>
                            <p className={styles.cellPrimary}>
                              {detailsRow.feeWaiverEnabled ? "Yes" : "No"}
                            </p>
                          </div>
                          {detailsRow.feeWaiverEnabled && (
                            <div>
                              <p className={styles.statLabel}>Waiver Amount</p>
                              <p className={styles.cellPrimary}>
                                {formatCurrency(detailsRow.feeWaiverAmount)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {!detailsRow.isAddOnType && (
                        <div className={styles.formSection}>
                          <p className={styles.formSectionTitle}>
                            <FiPackage style={{ marginRight: 6, verticalAlign: "-2px" }} />
                            Add-ons ({detailsRow.addOns.length})
                          </p>
                          {detailsRow.addOns.length === 0 ? (
                            <p className={styles.cellSecondary}>No add-ons attached to this plan.</p>
                          ) : (
                            <div className={styles.tableScroll}>
                              <table className={styles.table}>
                                <thead>
                                  <tr>
                                    <th>Add-on</th>
                                    <th>Value</th>
                                    <th>Amount</th>
                                    <th>Duration</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {detailsRow.addOns.map((addOn) => (
                                    <tr key={addOn.addOnId ?? addOn.key}>
                                      <td style={{ textTransform: "capitalize" }}>{addOn.label}</td>
                                      <td>{addOn.value ?? "\u2014"}</td>
                                      <td>{formatCurrency(addOn.amount)}</td>
                                      <td>{addOn.duration || "\u2014"}</td>
                                      <td>{formatDate(addOn.startDate)}</td>
                                      <td>{formatDate(addOn.endDate)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}

                      <div className={styles.formSection} style={{ marginBottom: 0 }}>
                        <p className={styles.formSectionTitle}>Timestamps</p>
                        <div className={styles.detailsGrid}>
                          <div>
                            <p className={styles.statLabel}>Created</p>
                            <p className={styles.cellPrimary}>{formatDateTime(detailsRow.created)}</p>
                          </div>
                          <div>
                            <p className={styles.statLabel}>Updated</p>
                            <p className={styles.cellPrimary}>{formatDateTime(detailsRow.updated)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.modalFooter}>
                      {/* <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => setDetailsRow(null)}
                      >
                        Close
                      </button> */}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <AdminFooter />
      </div>
    </div>
  );
};

export default UserPlanList;